import React, {
  isValidElement,
  ReactNode,
  useEffect,
  useState,
  ChangeEventHandler
} from "react";
import { flatMap } from "lodash";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Feedback from "react-bootstrap/Feedback";
import { FormControlProps } from "react-bootstrap/esm/FormControl";
import clsx from "clsx";

import Button from "../../components/button/Button";
import { field } from "../../forms";
import { RenderWhen, ConditionalTooltip } from "../../components";

type ButtonLabelsType = {
  action: string;
  cancel: string;
};

export interface FieldProps<T> extends FormControlProps {
  id: string;
  name: string;
  label: string | ReactNode;
  labelBold?: boolean;
  help?: ReactNode | string;
  helpImportant?: string;
  className?: string;
  hidden?: boolean;
  errors?: string[];
  isValueSet?: boolean;
  values?: T;
  setFieldValue?: (
    values: T | undefined,
    valueSetName: string,
    newIsEditMode: boolean
  ) => void;
  skipColon?: boolean;
  vertical: boolean;
  marginBottom?: number;
  buttonLabels?: ButtonLabelsType;
  tooltip?: {
    message: string;
    show: boolean;
    className?: string;
    wide: boolean;
  };
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

interface ChangeButtonProps {
  isEditMode?: boolean;
  onClick: () => void;
  labels?: ButtonLabelsType;
}

const getHelpText = (help: ReactNode | string, isValueSet?: boolean) => {
  // Additional help text for this field, so it's clear to the user how to clear the field.
  let helpText = "";
  const helpChangeButton =
    "To remove a value that has already been configured, click the Change button and leave this input empty.";
  if (help && isValueSet) {
    helpText = `${help} ${helpChangeButton}`;
  } else if (isValueSet) {
    helpText = helpChangeButton;
  }
  return helpText;
};

const ChangeButton: React.FC<ChangeButtonProps> = ({
  isEditMode,
  onClick,
  labels = {
    action: "Change",
    cancel: "Cancel"
  }
}) => {
  const { action: actionLabel, cancel: cancelLabel } = labels;
  return (
    <Button
      onClick={onClick}
      title={isEditMode ? actionLabel : cancelLabel}
      variant="secondary"
      className="ms-25"
    >
      {isEditMode ? actionLabel : cancelLabel}
    </Button>
  );
};

const WidgetPassword = <T,>(props: FieldProps<T>) => {
  const {
    id,
    label,
    labelBold,
    help,
    helpImportant,
    errors = [],
    skipColon,
    vertical = false,
    marginBottom = 25,
    name,
    isValueSet,
    setFieldValue,
    values,
    buttonLabels,
    tooltip,
    onChange,
    ...extra
  } = props;
  const [showChangeButton, setShowChangeButton] = useState(isValueSet);
  const [isEditMode, setIsEditMode] = useState(isValueSet);
  const placeholder = Array(9).join("\u25CF");
  const helpText = getHelpText(help, isValueSet);
  const valueSetName = `${name}_set`;

  const handleEditClick = () => {
    const newIsEditMode = !isEditMode;
    setIsEditMode(newIsEditMode);
    setFieldValue?.(values, valueSetName, newIsEditMode);
  };

  let filteredErrors;
  if (typeof errors === "object" && errors !== null) {
    // turn numeric keyed object into a list
    filteredErrors = flatMap(errors, value => value);
  } else {
    filteredErrors = errors;
  }
  const hasErrors = filteredErrors?.length;

  const widgetClass = clsx(props.className, {
    "has-error": hasErrors,
    hidden: props.hidden
  });

  useEffect(() => {
    // Change button needs to use the initial value, as we'll change isValueSet during editMode. This prevents
    // the button from disappearing during this change.
    if (isValueSet) {
      setShowChangeButton(isValueSet);
    }
    setIsEditMode(isValueSet);
  }, [isValueSet]);

  const complexLabel = isValidElement(label);
  const labelContainerClassName = clsx({ "mb-5": vertical });

  return (
    <Form.Group
      as={vertical ? "div" : Row}
      className={`${widgetClass} mb-${marginBottom}`}
    >
      <Col xs={vertical ? 12 : 3} className={labelContainerClassName}>
        <RenderWhen condition={complexLabel}>{label}</RenderWhen>
        <RenderWhen condition={!complexLabel && !!label}>
          <Form.Label className={clsx(labelBold && "form-label--bold")}>
            {skipColon ? `${label}` : `${label}:`}
          </Form.Label>
        </RenderWhen>
      </Col>
      <div className={clsx("d-flex", vertical ? "col-12" : "col-9")}>
        <Col
          xs={showChangeButton ? 10 : 12}
          className={
            showChangeButton
              ? "admin-page-input-width flex-shrink-1"
              : undefined
          }
        >
          <ConditionalTooltip
            condition={!!tooltip?.show}
            tooltip={tooltip?.message || ""}
            className={tooltip?.className}
            wide={tooltip?.wide}
            placement="bottom-start"
          >
            <Form.Control
              id={id}
              name={name}
              disabled={isEditMode}
              placeholder={isEditMode ? placeholder : ""}
              type="password"
              onChange={onChange}
              autoComplete="new-password"
              {...extra}
            />
          </ConditionalTooltip>
          <Feedback type="invalid">{filteredErrors}</Feedback>
          {helpText && <Form.Text>{helpText}</Form.Text>}
          {helpImportant && (
            <Form.Text className="form-text--important">
              {helpImportant}
            </Form.Text>
          )}
        </Col>
        {showChangeButton ? (
          <ChangeButton
            isEditMode={isEditMode}
            onClick={handleEditClick}
            labels={buttonLabels}
          />
        ) : null}
      </div>
    </Form.Group>
  );
};

const FieldWidgetPassword = field(WidgetPassword);
export { FieldWidgetPassword, WidgetPassword };
